import React, { useState, useEffect } from 'react';
import { 
  FeatherPhone,
  FeatherMail,
  FeatherClock,
  FeatherCheck,
  FeatherX,
  FeatherAlertCircle,
  FeatherArrowRight,
  FeatherCalendar,
  FeatherUser,
  FeatherMessageSquare,
  FeatherGlobe
} from '../ui/icons';
import api from '../../services/api';
import './ContactFlowChart.css';

const ContactFlowChart = ({ partnerId, onClose, onBack, embedded = false }) => {
  const [contactHistory, setContactHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [viewMode, setViewMode] = useState('timeline'); // timeline, flow, summary

  useEffect(() => {
    fetchContactHistory();
  }, [partnerId]);

  const fetchContactHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/contact-attempts/partner/${partnerId}/history`);
      setContactHistory(response.data.history || []);
    } catch (err) {
      console.error('Error fetching contact history:', err);
      setError('Failed to load contact history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status, method) => {
    const iconMap = {
      // Phone statuses
      'phone_called': <FeatherPhone size={16} />,
      'phone_busy': <FeatherPhone size={16} />,
      'phone_no_answer': <FeatherPhone size={16} />,
      'call_back_requested': <FeatherClock size={16} />,
      'wrong_number': <FeatherAlertCircle size={16} />,
      
      // Email statuses
      'email_sent': <FeatherMail size={16} />,
      'email_no_response': <FeatherMail size={16} />,
      'email_bounced': <FeatherAlertCircle size={16} />,
      'email_spam_filtered': <FeatherAlertCircle size={16} />,
      
      // Outcomes
      'interested': <FeatherCheck size={16} />,
      'not_interested': <FeatherX size={16} />,
      'permanently_unreachable': <FeatherX size={16} />,
      'rejected': <FeatherX size={16} />,
      
      // Other methods
      'linkedin': <FeatherGlobe size={16} />,
      'whatsapp': <FeatherMessageSquare size={16} />
    };
    
    return iconMap[status] || iconMap[method] || <FeatherAlertCircle size={16} />;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      // Success outcomes
      'interested': 'success',
      
      // Negative outcomes
      'not_interested': 'danger',
      'permanently_unreachable': 'danger',
      'rejected': 'danger',
      
      // Warning/Retry needed
      'phone_busy': 'warning',
      'phone_no_answer': 'warning',
      'email_no_response': 'warning',
      'call_back_requested': 'warning',
      
      // Info/Neutral
      'phone_called': 'info',
      'email_sent': 'info',
      'wrong_number': 'neutral',
      'email_bounced': 'neutral',
      'email_spam_filtered': 'neutral'
    };
    
    return colorMap[status] || 'neutral';
  };

  const getStatusLabel = (status) => {
    const labelMap = {
      'phone_called': 'Called',
      'phone_busy': 'Phone Busy',
      'phone_no_answer': 'No Answer',
      'call_back_requested': 'Call Back Requested',
      'wrong_number': 'Wrong Number',
      'email_sent': 'Email Sent',
      'email_no_response': 'No Response',
      'email_bounced': 'Email Bounced',
      'email_spam_filtered': 'Spam Filtered',
      'interested': 'Interested',
      'not_interested': 'Not Interested',
      'permanently_unreachable': 'Unreachable',
      'rejected': 'Rejected'
    };
    
    return labelMap[status] || status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAttemptStats = () => {
    const stats = {
      total: contactHistory.length,
      phone: contactHistory.filter(attempt => attempt.contact_method === 'phone').length,
      email: contactHistory.filter(attempt => attempt.contact_method === 'email').length,
      other: contactHistory.filter(attempt => !['phone', 'email'].includes(attempt.contact_method)).length,
      successful: contactHistory.filter(attempt => attempt.status === 'interested').length,
      failed: contactHistory.filter(attempt => ['not_interested', 'permanently_unreachable', 'rejected'].includes(attempt.status)).length,
      pending: contactHistory.filter(attempt => ['phone_busy', 'phone_no_answer', 'email_no_response', 'call_back_requested'].includes(attempt.status)).length
    };
    
    return stats;
  };

  const renderTimelineView = () => (
    <div className="flow-chart-timeline">
      {contactHistory.map((attempt, index) => (
        <div key={attempt.id} className="timeline-item">
          <div className="timeline-connector">
            {index < contactHistory.length - 1 && <div className="timeline-line" />}
          </div>
          
          <div 
            className={`timeline-node ${getStatusColor(attempt.status)}`}
            onClick={() => setSelectedAttempt(attempt)}
          >
            {getStatusIcon(attempt.status, attempt.contact_method)}
          </div>
          
          <div className="timeline-content">
            <div className="timeline-header">
              <h4>{getStatusLabel(attempt.status)}</h4>
              <span className="timeline-date">{formatDate(attempt.created_at)}</span>
            </div>
            
            <div className="timeline-details">
              <p className="timeline-method">
                {attempt.contact_method === 'phone' ? '📞 Phone' : 
                 attempt.contact_method === 'email' ? '📧 Email' : 
                 `🌐 ${attempt.contact_method}`}
              </p>
              
              {attempt.contact_person_name && (
                <p className="timeline-contact">
                  <FeatherUser size={12} />
                  {attempt.contact_person_name}
                  {attempt.contact_person_role && ` (${attempt.contact_person_role})`}
                </p>
              )}
              
              {attempt.content && (
                <p className="timeline-notes">{attempt.content}</p>
              )}
              
              {attempt.notes && (
                <p className="timeline-additional-notes">
                  <strong>Notes:</strong> {attempt.notes}
                </p>
              )}
            </div>
            
            {attempt.follow_ups && attempt.follow_ups.length > 0 && (
              <div className="timeline-followups">
                <div className="followup-indicator">
                  <FeatherClock size={12} />
                  {attempt.follow_ups.length} follow-up{attempt.follow_ups.length > 1 ? 's' : ''}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderFlowView = () => (
    <div className="flow-chart-flow">
      <div className="flow-container">
        {contactHistory.map((attempt, index) => (
          <div key={attempt.id} className="flow-step">
            <div 
              className={`flow-node ${getStatusColor(attempt.status)}`}
              onClick={() => setSelectedAttempt(attempt)}
            >
              <div className="flow-icon">
                {getStatusIcon(attempt.status, attempt.contact_method)}
              </div>
              <div className="flow-label">{getStatusLabel(attempt.status)}</div>
              <div className="flow-date">{formatDate(attempt.created_at)}</div>
            </div>
            
            {index < contactHistory.length - 1 && (
              <div className="flow-arrow">
                <FeatherArrowRight size={20} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderSummaryView = () => {
    const stats = getAttemptStats();
    
    return (
      <div className="flow-chart-summary">
        <div className="summary-stats">
          <div className="stat-card total">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Attempts</div>
          </div>
          
          <div className="stat-card phone">
            <div className="stat-number">{stats.phone}</div>
            <div className="stat-label">Phone Calls</div>
          </div>
          
          <div className="stat-card email">
            <div className="stat-number">{stats.email}</div>
            <div className="stat-label">Emails</div>
          </div>
          
          <div className="stat-card successful">
            <div className="stat-number">{stats.successful}</div>
            <div className="stat-label">Successful</div>
          </div>
          
          <div className="stat-card failed">
            <div className="stat-number">{stats.failed}</div>
            <div className="stat-label">Failed</div>
          </div>
          
          <div className="stat-card pending">
            <div className="stat-number">{stats.pending}</div>
            <div className="stat-label">Pending</div>
          </div>
        </div>
        
        <div className="summary-chart">
          <h4>Contact Method Distribution</h4>
          <div className="method-distribution">
            <div className="method-bar">
              <div className="method-label">Phone</div>
              <div className="method-progress">
                <div 
                  className="method-fill phone" 
                  style={{ width: `${(stats.phone / stats.total) * 100}%` }}
                />
              </div>
              <div className="method-count">{stats.phone}</div>
            </div>
            
            <div className="method-bar">
              <div className="method-label">Email</div>
              <div className="method-progress">
                <div 
                  className="method-fill email" 
                  style={{ width: `${(stats.email / stats.total) * 100}%` }}
                />
              </div>
              <div className="method-count">{stats.email}</div>
            </div>
            
            <div className="method-bar">
              <div className="method-label">Other</div>
              <div className="method-progress">
                <div 
                  className="method-fill other" 
                  style={{ width: `${(stats.other / stats.total) * 100}%` }}
                />
              </div>
              <div className="method-count">{stats.other}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`flow-chart-container ${embedded ? 'embedded' : ''}`}>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading contact history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flow-chart-container ${embedded ? 'embedded' : ''}`}>
        <div className="error-state">
          <FeatherAlertCircle size={48} />
          <p>{error}</p>
          <button onClick={fetchContactHistory}>Retry</button>
        </div>
      </div>
    );
  }

  // Embedded mode - render without modal wrapper
  if (embedded) {
    return (
      <div className="flow-chart-embedded">
        <div className="flow-chart-header-embedded">
          <div className="view-controls">
            <button 
              className={`view-button ${viewMode === 'timeline' ? 'active' : ''}`}
              onClick={() => setViewMode('timeline')}
            >
              Timeline
            </button>
            <button 
              className={`view-button ${viewMode === 'flow' ? 'active' : ''}`}
              onClick={() => setViewMode('flow')}
            >
              Flow
            </button>
            <button 
              className={`view-button ${viewMode === 'summary' ? 'active' : ''}`}
              onClick={() => setViewMode('summary')}
            >
              Summary
            </button>
          </div>
          <div className="embedded-header-actions">
            {onBack && (
              <button className="back-button" onClick={onBack}>
                <FeatherArrowRight size={20} /> Back
              </button>
            )}
            {/* <button className="close-button" onClick={onClose}>×</button> */}
          </div>
        </div>
        
        <div className="flow-chart-body-embedded">
          {viewMode === 'timeline' && renderTimelineView()}
          {viewMode === 'flow' && renderFlowView()}
          {viewMode === 'summary' && renderSummaryView()}
        </div>
        
        {selectedAttempt && (
          <div className="attempt-details-modal">
            <div className="attempt-details-content">
              <div className="attempt-details-header">
                <h4>Contact Attempt Details</h4>
                <button onClick={() => setSelectedAttempt(null)}>×</button>
              </div>
              <div className="attempt-details-body">
                <div className="detail-row">
                  <strong>Status:</strong> {getStatusLabel(selectedAttempt.status)}
                </div>
                <div className="detail-row">
                  <strong>Method:</strong> {selectedAttempt.contact_method}
                </div>
                <div className="detail-row">
                  <strong>Date:</strong> {formatDate(selectedAttempt.created_at)}
                </div>
                {selectedAttempt.contact_person_name && (
                  <div className="detail-row">
                    <strong>Contact:</strong> {selectedAttempt.contact_person_name}
                  </div>
                )}
                {selectedAttempt.content && (
                  <div className="detail-row">
                    <strong>Content:</strong> {selectedAttempt.content}
                  </div>
                )}
                {selectedAttempt.notes && (
                  <div className="detail-row">
                    <strong>Notes:</strong> {selectedAttempt.notes}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full modal mode
  return (
    <div className="flow-chart-modal">
      <div className="flow-chart-overlay">
        <div className="flow-chart-content">
          <div className="flow-chart-header">
            <h3>📊 Contact Flow Chart</h3>
            <div className="view-controls">
              <button 
                className={`view-button ${viewMode === 'timeline' ? 'active' : ''}`}
                onClick={() => setViewMode('timeline')}
              >
                Timeline
              </button>
              <button 
                className={`view-button ${viewMode === 'flow' ? 'active' : ''}`}
                onClick={() => setViewMode('flow')}
              >
                Flow
              </button>
              <button 
                className={`view-button ${viewMode === 'summary' ? 'active' : ''}`}
                onClick={() => setViewMode('summary')}
              >
                Summary
              </button>
            </div>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          
          <div className="flow-chart-body">
            {viewMode === 'timeline' && renderTimelineView()}
            {viewMode === 'flow' && renderFlowView()}
            {viewMode === 'summary' && renderSummaryView()}
          </div>
          
          {selectedAttempt && (
            <div className="attempt-details-modal">
              <div className="attempt-details-content">
                <div className="attempt-details-header">
                  <h4>Contact Attempt Details</h4>
                  <button onClick={() => setSelectedAttempt(null)}>×</button>
                </div>
                <div className="attempt-details-body">
                  <div className="detail-row">
                    <strong>Status:</strong> {getStatusLabel(selectedAttempt.status)}
                  </div>
                  <div className="detail-row">
                    <strong>Method:</strong> {selectedAttempt.contact_method}
                  </div>
                  <div className="detail-row">
                    <strong>Date:</strong> {formatDate(selectedAttempt.created_at)}
                  </div>
                  {selectedAttempt.contact_person_name && (
                    <div className="detail-row">
                      <strong>Contact:</strong> {selectedAttempt.contact_person_name}
                    </div>
                  )}
                  {selectedAttempt.content && (
                    <div className="detail-row">
                      <strong>Content:</strong> {selectedAttempt.content}
                    </div>
                  )}
                  {selectedAttempt.notes && (
                    <div className="detail-row">
                      <strong>Notes:</strong> {selectedAttempt.notes}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactFlowChart; 